/**
 * Coach's vocabulary, in one place so the card and the drawer never describe the
 * same squad in two different registers.
 *
 * Two rules govern everything here:
 *
 *  - The stalled flag is a doorbell, not a diagnosis. Until paired-rater
 *    agreement is actually run, the surface says "quiet for a while, worth a
 *    look" and never "stuck", and it has exactly two states: raised, or absent.
 *    There is no on-track, no doing-great, no green.
 *  - Every figure names where it came from and when it was read, because a
 *    number on a live surface with no provenance invites being trusted past its
 *    freshness.
 */

/**
 * THE CHECK-IN HOUSE RULES, which every builder below obeys and which are the
 * first thing to check when editing any of them (spec section 4):
 *
 *  - lowercase by default; capitalisation happens in CSS, on labels only.
 *  - no em dashes, anywhere.
 *  - no second person about the student. The facilitator is being told a fact
 *    about a row; the student is never addressed and never described.
 *  - no verb that characterises a person. The sentence says what did not
 *    happen, never why, never what kind of person would let it happen.
 *  - no coaching question renders at all in v1.5 (spec 2.3). `prompt_id` is
 *    null until an authored, integrity-reviewed prompt exists for the exact
 *    reason it would accompany, and there is no fallback question.
 */

export const ARTIFACT_STATES = [
  "planning",
  "executing",
  "finishing",
  "submitted",
  "graded",
];

export const STALLED_COPY = "quiet for a while, worth a look";

export const artifactStateLabel = (state) =>
  ARTIFACT_STATES.includes(state) ? state : "planning";

/** "5 tasks, 3 done". Singular where singular is correct. */
export const taskCounterCopy = (created, completed) => {
  const total = Number(created) || 0;
  const done = Number(completed) || 0;
  return `${total} ${total === 1 ? "task" : "tasks"}, ${done} done`;
};

/** Recency in plain words. Null in, null out: no invented "never". */
export const relativeTime = (iso, now = Date.now()) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hr" : "hrs"} ago`;

  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
};

/** The clock time a figure was read, for the per-figure refresh labels. */
export const readAt = (generatedAt) => {
  if (!generatedAt) return "";
  const at = new Date(generatedAt);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

/** "from task records, read 10:42". */
export const sourceLabel = (source, generatedAt) => {
  const at = readAt(generatedAt);
  return at ? `from ${source}, read ${at}` : `from ${source}`;
};

export const activityStateLabel = (state) => {
  if (state === "closed") return "done";
  if (state === "no_activity_recorded") return "no activity recorded yet";
  if (state === "quiet") return "quiet";
  if (state === "active") return "active";
  return "activity not reported";
};

export const stalledAttentionCopy = (lastActivityAt) => {
  const elapsed = relativeTime(lastActivityAt);
  return elapsed ? `quiet ${elapsed}, worth a look` : STALLED_COPY;
};

/*
|------------------------------------------------------------------------------
| The check-in exception layer (spec section 4)
|------------------------------------------------------------------------------
*/

/**
 * THREE STATES, and the distinction between two of them is the whole point.
 * Only `ready` licenses reading an empty list as "there is nothing to show".
 * `disabled` means the layer is off and the slot renders NOTHING at all: an
 * empty state there would read as "all clear", which is a positive-state
 * certification this surface is not allowed to make from data it never
 * computed. `unavailable` means the computation could not complete, and it
 * gets a named gap line rather than silence.
 */
export const CHECKIN_STATUS = {
  DISABLED: "disabled",
  READY: "ready",
  UNAVAILABLE: "unavailable",
};

export const CHECKIN_UNAVAILABLE_COPY =
  "check-ins could not be computed on this refresh.";

/**
 * `level_identity`, the four values `SquadLevelIdentityResolver` emits. This is
 * the field every level claim on this surface keys on.
 */
export const LEVEL_IDENTITY = {
  EVIDENCED: "evidenced",
  PRESUMED: "presumed",
  AWAITING_NEXT: "awaiting_next",
  UNAVAILABLE: "unavailable",
};

/**
 * THE IDENTITIES THAT LICENSE STATING A SQUAD'S LEVEL, and the reason this is a
 * string test rather than a null test.
 *
 * The backend does NOT signal "no level" with a null capsule. Under `presumed`
 * the resolver returns `$ordered->first()` with `level_number` 1, because an
 * untouched squad still has to be represented. So gating a level claim on the
 * capsule being absent is vacuous: an untouched room would render "squads on
 * level 1" and a level 1 chip on every card, which is a guess presented as a
 * location. That guess is precisely the presumed-level defect behavior change
 * A1 removed from the numbers, and it must not come back as a label.
 *
 * `awaiting_next` IS included. Its capsule is the level the squad finished and
 * most recently submitted, which the resolver anchors there deliberately so the
 * card keeps showing a completed deliverable rather than jumping to a level
 * with no evidence. That is a real, evidenced location.
 *
 * `presumed` and `unavailable` are excluded. The card's state chip
 * (planning, and its task counts) carries the honesty for those squads.
 */
export const LEVEL_CLAIMABLE_IDENTITIES = [
  LEVEL_IDENTITY.EVIDENCED,
  LEVEL_IDENTITY.AWAITING_NEXT,
];

export const claimsLevel = (levelIdentity) =>
  LEVEL_CLAIMABLE_IDENTITIES.includes(levelIdentity);

/**
 * THE IDENTITIES A CHECK-IN RESULT CAN BE READ FOR (names-layer sprint, PR B).
 *
 * `awaiting_next` used to be excluded here, and that exclusion is the defect
 * Codex F1 named: a squad that finished its level and left a member behind was
 * the exact shape the layer exists to catch, and the panel refused to read its
 * entries at all. PR B admits those squads server side; this is the client half
 * of the same change.
 *
 * IT IS ADMITTED ONLY WITH AN EXPLICIT `checkin_status: "ready"`, and that
 * condition is not decoration. Before PR B an `awaiting_next` squad was
 * excluded at the backend's STEP 1 and serialized with the SAME empty `entries`
 * a computed squad gets, so reading a payload from a server that predates the
 * field as "computed, and there is nothing" would state a clean result for a
 * computation that never ran. Frontends and backends deploy independently, so
 * that server is not hypothetical for the length of a rollout. Absent is not
 * ready; the panel says it has no result rather than saying there is nothing.
 *
 * `evidenced` keeps its pre-existing behaviour under an absent status, because
 * for that identity a legacy server DID compute, and tightening it would darken
 * every squad on the surface for the duration of a deploy.
 */
export const checkinEvaluable = (checkins) => {
  const identity = checkins?.level_identity;
  if (identity === LEVEL_IDENTITY.EVIDENCED) return true;
  return (
    identity === LEVEL_IDENTITY.AWAITING_NEXT &&
    checkins?.checkin_status === CHECKIN_STATUS.READY
  );
};

/**
 * A squad that was not evaluated gets a line saying WHY, because "no check-ins
 * in this squad" would be a claim about a computation that did not happen. The
 * reasons are genuinely different states and one sentence cannot be true of all
 * of them:
 *
 *  - `presumed`: nothing has been touched. There is no evidenced level.
 *  - `unavailable`: the program carries no levels at all, which is a setup
 *    fact about the program and not a fact about this squad.
 *
 * `awaiting_next` IS NO LONGER IN THIS MAP, and its removal is a behaviour
 * change worth stating. It used to render "check-ins resume when the next level
 * is evidenced", which was true of the platform that wrote it and is false of
 * the platform PR B ships: check-ins do not wait for the next level any more,
 * they are computed against the level the squad finished. An `awaiting_next`
 * squad now either renders its entries (see `checkinEvaluable`) or falls to
 * `CHECKIN_NO_RESULT_COPY`, which claims nothing about levels at all and is
 * true of both a legacy payload and one this build cannot read.
 *
 * None of these says "on track", "all clear", or anything else positive: each
 * states what has not happened, and what would change it.
 */
const NOT_EVIDENCED_COPY = {
  [LEVEL_IDENTITY.PRESUMED]:
    "no level is evidenced for this squad yet, so no check-ins are computed.",
  [LEVEL_IDENTITY.UNAVAILABLE]:
    "this program has no levels, so no check-ins are computed.",
};

/**
 * No per-squad object at all, which the controller's
 * `$checkinsBySquad[$squadId] ?? null` fallback can produce even under a `ready`
 * envelope. This is NOT the same as a squad that resolved to `presumed`, and
 * saying "no level is evidenced for this squad yet" about it would be inventing
 * a specific finding out of a missing result.
 */
export const CHECKIN_NO_RESULT_COPY =
  "no check-in result came back for this squad.";

/**
 * A missing or unrecognized identity gets the no-result line, never a borrowed
 * one. Falling back to the `presumed` sentence looked conservative, because
 * `presumed` claims the least of the three real states, but it is still a claim
 * ABOUT THE SQUAD'S LEVEL made from the absence of any information. Naming the
 * absence is the honest floor.
 */
export const notEvidencedCopy = (levelIdentity) =>
  NOT_EVIDENCED_COPY[levelIdentity] || CHECKIN_NO_RESULT_COPY;

export const CHECKIN_NONE_COPY = "no check-ins in this squad.";

/**
 * A checkpoint this build has no sentence for. It is reachable the moment the
 * server adds one (`task_defined` and `assigned_task_completed` are both named
 * v1.6 candidates) and the frontend has not caught up. Rendering the raw enum
 * key, or silently dropping an entry the server counted, are both worse: one
 * puts machine text in front of a facilitator, the other makes the card's count
 * disagree with the drawer for a reason nobody can see.
 */
export const CHECKIN_UNRECOGNIZED_COPY =
  "a check-in this view does not recognize yet.";

/**
 * THE MEMBERS SECTION'S THREE SENTENCES, and there is deliberately no fourth
 * and no builder.
 *
 * They live here for the reason the file's header gives: one vocabulary, so the
 * card and the drawer cannot describe one squad in two registers. All lowercase,
 * no em dash, no second person, no verb characterising a person.
 *
 * WHY THE TWO FAILURE SENTENCES ARE DIFFERENT SENTENCES. They name two different
 * failures. Under the roster tier the names themselves are gone and nothing
 * about who is in the squad is known; under the positions tier the names are
 * known and only the positions failed. One sentence covering both would be
 * wrong half the time, which is the exact distinction `CheckinPanel` was built
 * around.
 *
 * `MEMBERS_NONE_COPY` is a ROSTER FACT, not a clean bill of health. It does not
 * say "no members yet" (a claim about setup) and it does not say "all clear".
 *
 * There is NO `memberPositionCopy()`. The position line is composed server side
 * and rendered verbatim; a second composer here would drift from the backend's
 * `training N` counter invisibly, until a facilitator read a number no student
 * has ever seen.
 */
export const MEMBERS_NONE_COPY = "no current members in this squad.";
export const MEMBERS_ROSTER_UNAVAILABLE_COPY =
  "the roster could not be read on this refresh.";
export const MEMBERS_POSITIONS_UNAVAILABLE_COPY =
  "positions could not be read on this refresh.";

const CHECKPOINT_LABELS = {
  reflection_submitted: "reflection",
  progress_report_submitted: "progress report",
};

export const checkpointLabel = (checkpoint) =>
  CHECKPOINT_LABELS[checkpoint] || null;

/** "2 check-ins". Nothing at zero: the card has no counterpart "all clear". */
export const checkinCountCopy = (count) => {
  const n = Number(count) || 0;
  if (n < 1) return null;
  return `${n} ${n === 1 ? "check-in" : "check-ins"}`;
};

/** "+1 more check-in in this squad". A fact on screen, never a hidden state. */
export const overflowCopy = (count) => {
  const n = Number(count) || 0;
  if (n < 1) return null;
  return `+${n} more ${n === 1 ? "check-in" : "check-ins"} in this squad`;
};

/**
 * THE DURATION GRAIN CHANGED WITH THE CHECKPOINT-INVARIANT AMENDMENT, and this
 * is where that shows.
 *
 * The spec body's copy said `first seen 2 days ago`, because its clock started
 * at the first poll that happened to observe an open gap. The amendment
 * (2026-07-29) deletes that ledger: both crossings are now server-stamped and
 * monotonic, so the backend anchors `days_observed` at a RECORDED CROSSING
 * TIME instead (`majorityCrossedAt` for an individual entry, the first crossing
 * for a squad entry). "first seen" would now be a false sentence about the
 * number actually being rendered, so the copy names the crossing the number is
 * measured from.
 *
 * `today` rather than "0 days ago" because `days_observed` is a floor and can
 * legitimately be 0 if `coach.checkin_sustained_hours` is ever configured below
 * 24. At the shipped 48-hour default it is always at least 2.
 */
export const dayCountCopy = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n === 0) return "today";
  return `${n} ${n === 1 ? "day" : "days"} ago`;
};

/**
 * A level number this surface may state, or null.
 *
 * ONE DEFINITION, USED BY EVERY LEVEL CLAIM, because "what counts as a level"
 * drifting between the chip, the header summary and the drawer sentence is how
 * a fabricated number reaches one of them.
 *
 * Two ways a non-level slips through, and coercion hides both:
 *
 *  1. `Number(null)` is `0` and `Number.isFinite(0)` is true, so a null
 *     `level_number` on a present capsule coerced straight into "level 0".
 *  2. Zero is not a level at all. The resolver numbers levels from 1
 *     (`ordinal + 1`), and the backend serializes through `(int) $levelNumber`,
 *     which turns a null into exactly `0`. That path is unreachable today
 *     because a non-null capsule always carries an ordinal, but the cast is one
 *     refactor away from emitting the one value that is guaranteed to be wrong.
 *
 * So: a real, positive integer, tested BEFORE coercion. Anything else is not a
 * level and the caller says nothing rather than inventing one.
 */
export const validLevelNumber = (value) =>
  typeof value === "number" && Number.isInteger(value) && value >= 1
    ? value
    : null;

/**
 * THE CARRIED ENTRY'S CONTEXT CLAUSE, AND WHY IT IS IDENTITY-AWARE (Codex flat
 * review, PR #396).
 *
 * `evaluation_capsule` means DIFFERENT THINGS under the two evaluable
 * identities. Under `evidenced` it is the level the squad is working. Under
 * `awaiting_next` it is the level they FINISHED and submitted, which the
 * resolver anchors there on purpose so the card keeps showing a completed
 * deliverable. One sentence therefore cannot be true of both, and the wrong half
 * was loud: "the squad is working level 6" about a squad the room map has
 * standing in the turned-in column, one click away, is the surface disagreeing
 * with itself about the same squad in the same glance.
 *
 * `turned in` is `statusVocabulary`'s term for that stage and the word the
 * matrix column, the drawer's state strip and the review queue all use, so this
 * clause joins them rather than inventing a fourth phrasing for one position.
 *
 * AN IDENTITY THIS MAP DOES NOT COVER OMITS THE CLAUSE. `checkinEvaluable`
 * admits only these two, so no rendered entry can reach the miss; a direct call
 * that does gets silence rather than a borrowed claim about what a squad is
 * doing with a level.
 */
const CARRIED_CONTEXT = {
  [LEVEL_IDENTITY.EVIDENCED]: (level) => `the squad is working level ${level}.`,
  [LEVEL_IDENTITY.AWAITING_NEXT]: (level) =>
    `the squad turned in level ${level}.`,
};

const levelPhrase = (entry) => {
  const level = validLevelNumber(entry?.gap_capsule?.level_number);
  const label = checkpointLabel(entry?.checkpoint);
  if (level === null || !label) return null;
  return `level ${level} ${label}`;
};

/**
 * Every sentence of one entry, or null when the entry cannot be phrased
 * honestly. Null is the caller's cue to render CHECKIN_UNRECOGNIZED_COPY.
 *
 * THE LEVEL NUMBER COMES FROM `gap_capsule` AND FROM NOWHERE ELSE (spec 2.1b).
 * The squad's own `evaluation_capsule` lives on the enclosing object under a
 * different name and reaches exactly one clause, the explicitly labelled "the
 * squad is working level N" context line on a carried entry. Reason and
 * resolution are rendered from the same field, so they can never name
 * different levels.
 */
export const checkinSentences = (
  entry,
  evaluationLevelNumber = null,
  { concealed = false, levelIdentity = null } = {},
) => {
  const phrase = levelPhrase(entry);
  if (!phrase) return null;

  const elapsed = dayCountCopy(entry?.days_observed);

  if (entry?.scope === "squad") {
    return {
      subject: null,
      reason: `most of the squad has not submitted the ${phrase}.`,
      context: null,
      // The squad predicate fires when NO majority exists, so there is no
      // majority instant to name. The backend anchors on the first crossing,
      // which is the same recorded fact that puts the checkpoint in play.
      elapsed: elapsed ? `the first submission landed ${elapsed}.` : null,
      resolves: `resolves when the rest of the squad submits the ${phrase}.`,
    };
  }

  const displayName = String(entry?.student?.display_name || "").trim();
  if (!displayName) return null;
  const name = concealed ? "one participant" : displayName;

  /* THE HOUSE CLASS, IN THE ONE CLAUSE THAT STILL HAD IT (CodeRabbit, PR #396).
     `Number(null)` is `0` and `Number.isFinite(0)` is true, so a squad whose
     evaluation capsule carried no level rendered "the squad is working level 0."
     on the named row AND in the drawer. Zero is not a level at all: the resolver
     numbers from 1, and the backend's `(int) $levelNumber` cast turns a null into
     exactly `0`, so it is the one value guaranteed to be wrong.

     `validLevelNumber` is the one definition every level claim on this surface
     already uses, and it tests BEFORE coercing. It is a straight swap for the
     coercion, which is what makes this the same fix the rest of the file made.

     AN INVALID LEVEL OMITS THE CLAUSE RATHER THAN SUBSTITUTING ANYTHING. The
     sentence is complete without it ("level 2 reflection not submitted."), and
     the context line is a labelled aside about the SQUAD, not part of the claim
     about the student. There is nothing to degrade to and nothing to say. */
  const squadLevel = validLevelNumber(evaluationLevelNumber);
  const phrasing = Object.prototype.hasOwnProperty.call(
    CARRIED_CONTEXT,
    levelIdentity,
  )
    ? CARRIED_CONTEXT[levelIdentity]
    : null;
  const context =
    entry?.carried && squadLevel !== null && phrasing
      ? phrasing(squadLevel)
      : null;

  return {
    subject: name,
    reason: `${phrase} not submitted.`,
    context,
    elapsed: elapsed ? `most of the squad submitted ${elapsed}.` : null,
    resolves: `resolves when ${name} submits the ${phrase}.`,
  };
};

/** The one-line preview form. Same clauses, same order, no new claim. */
export const checkinPreviewCopy = (
  entry,
  evaluationLevelNumber = null,
  options = {},
) => {
  const sentences = checkinSentences(entry, evaluationLevelNumber, options);
  if (!sentences) return null;

  return [
    sentences.subject,
    sentences.reason.replace(/\.$/, ""),
    sentences.elapsed ? sentences.elapsed.replace(/\.$/, "") : null,
  ]
    .filter(Boolean)
    .join(" · ");
};
